import { Alert, AutoComplete, Flex, Tag } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PartnerSearchType } from '@evimo/types/enums/partner-search-type';
import { usePartnerOrgAutocomplete } from '@/app/landing/sla/shared/hooks/usePartnerOrgAutocomplete';
import type { PartnerOrgSelection } from '@/app/landing/sla/shared/types';

export type { PartnerOrgSelection } from '@/app/landing/sla/shared/types';

type Props = {
  organizationId: string;
  searchType: PartnerSearchType;
  placeholder: string;
  partner: PartnerOrgSelection | null;
  onPartnerChange: (partner: PartnerOrgSelection | null) => void;
};

export function PartnerOrgAutocomplete({
  organizationId,
  searchType,
  placeholder,
  partner,
  onPartnerChange,
}: Props) {
  const { t } = useTranslation('sla');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { enrichPartner, isSearching, options, searchValue, setSearchValue } =
    usePartnerOrgAutocomplete(organizationId, searchType);

  const displayValue = partner?.name ?? searchValue;

  const autocompleteOptions = options.map((option) => ({
    value: option.value,
    name: option.name,
    alreadyLinked: option.alreadyLinked,
    disabled: option.alreadyLinked,
    label: (
      <Flex align="center" justify="space-between" gap={8}>
        <span>{option.name}</span>
        {option.alreadyLinked ? (
          <Tag color="default">{t('partnerSearch.alreadyLinked')}</Tag>
        ) : null}
      </Flex>
    ),
  }));

  const applyPartner = (nextPartner: PartnerOrgSelection | null) => {
    onPartnerChange(enrichPartner(nextPartner));
  };

  return (
    <Flex
      vertical
      gap={8}
      style={{ width: '100%' }}
      className="sla-partner-autocomplete"
    >
      <AutoComplete
        style={{ width: '100%' }}
        value={displayValue}
        options={autocompleteOptions}
        onDropdownVisibleChange={setIsDropdownOpen}
        popupClassName="sla-partner-autocomplete-popup"
        getPopupContainer={(triggerNode) =>
          triggerNode.parentElement ?? document.body
        }
        onSearch={(text) => {
          setSearchValue(text);
          if (!text.trim()) {
            applyPartner(null);
            return;
          }

          const matchedOption = options.find(
            (option) => option.name.toLowerCase() === text.trim().toLowerCase(),
          );

          if (matchedOption) {
            applyPartner({
              id: matchedOption.value,
              name: matchedOption.name,
            });
            return;
          }

          applyPartner({ name: text });
        }}
        onSelect={(value, option) => {
          const selected = option as {
            name?: string;
            alreadyLinked?: boolean;
          };

          if (selected.alreadyLinked) {
            applyPartner({
              id: value,
              name: selected.name ?? String(value),
            });
            return;
          }

          const name = selected.name ?? String(value);
          setSearchValue(name);
          applyPartner({ id: value, name });
        }}
        onChange={(text) => {
          setSearchValue(text);
          if (!text.trim()) {
            applyPartner(null);
            return;
          }

          const matchedOption = options.find(
            (option) => option.name.toLowerCase() === text.trim().toLowerCase(),
          );

          if (matchedOption) {
            applyPartner({
              id: matchedOption.value,
              name: matchedOption.name,
            });
            return;
          }

          applyPartner({ name: text });
        }}
        notFoundContent={
          isSearching ? t('partnerSearch.loading', 'Searching…') : undefined
        }
        placeholder={placeholder}
      />

      {partner?.alreadyLinked && !isDropdownOpen ? (
        <Alert
          type="warning"
          showIcon
          className="sla-partner-autocomplete-alert"
          title={t('partnerSearch.alreadyLinkedHint', {
            name: partner.name,
          })}
        />
      ) : null}
    </Flex>
  );
}
